class PredictiveSearch extends HTMLElement {
    constructor() {
        super();

        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector("#predictive-search");
        this.searchForm = this.querySelector('form[role="search"]');

        // Debounce delay in milliseconds
        this.debounceDelay = 300;

        // Minimum characters before triggering search
        this.minSearchLength = 2;

        // Current search timeout
        this.searchTimeout = null;

        // Bind methods
        this.onChange = this.onChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocus = this.onFocus.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onViewAllClick = this.onViewAllClick.bind(this);

        this.init();
    }

    init() {
        if (!this.input || !this.predictiveSearchResults) {
            console.warn("PredictiveSearch: Required elements not found");
            return;
        }

        // Add event listeners
        this.input.addEventListener(
            "input",
            this.debounce(this.onChange, this.debounceDelay)
        );
        this.input.addEventListener("keydown", this.onKeyDown);
        this.input.addEventListener("focus", this.onFocus);
        this.input.addEventListener("blur", this.onBlur);

        // Add click outside listener to close results
        document.addEventListener("click", this.handleClickOutside.bind(this));

        // Add view all button listener
        this.predictiveSearchResults.addEventListener(
            "click",
            this.onViewAllClick
        );
    }

    onChange(event) {
        const searchTerm = this.input.value.trim();

        if (searchTerm.length < this.minSearchLength) {
            this.close();
            return;
        }

        this.getSearchResults(searchTerm);
    }

    onKeyDown(event) {
        const results = this.predictiveSearchResults.querySelector(
            "#predictive-search-results"
        );
        if (!results || results.style.display === "none") return;

        const items = results.querySelectorAll('[role="option"]');
        const currentItem = results.querySelector('[aria-selected="true"]');
        let currentIndex = -1;

        if (currentItem) {
            currentIndex = Array.from(items).indexOf(currentItem);
        }

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                currentIndex =
                    currentIndex < items.length - 1 ? currentIndex + 1 : 0;
                this.setSelectedItem(items[currentIndex]);
                break;
            case "ArrowUp":
                event.preventDefault();
                currentIndex =
                    currentIndex > 0 ? currentIndex - 1 : items.length - 1;
                this.setSelectedItem(items[currentIndex]);
                break;
            case "Enter":
                event.preventDefault();
                if (currentItem) {
                    currentItem.querySelector("a").click();
                } else {
                    this.searchForm.submit();
                }
                break;
            case "Escape":
                event.preventDefault();
                this.close();
                this.input.blur();
                break;
        }
    }

    onFocus() {
        const searchTerm = this.input.value.trim();
        if (searchTerm.length >= this.minSearchLength) {
            this.open();
        }
    }

    onBlur() {
        // Delay closing to allow for clicks on results
        setTimeout(() => {
            // Only close if there's no text in the input
            if (!this.shouldStayOpen()) {
                this.close();
            }
        }, 150);
    }

    onViewAllClick(event) {
        if (event.target.matches("[data-predictive-search-view-all]")) {
            event.preventDefault();
            this.searchForm.submit();
        }
    }

    handleClickOutside(event) {
        if (!this.contains(event.target)) {
            // Only close if there's no text in the input
            if (!this.shouldStayOpen()) {
                this.close();
            }
        }
    }

    setSelectedItem(item) {
        // Remove previous selection
        const previousSelected = this.predictiveSearchResults.querySelector(
            '[aria-selected="true"]'
        );
        if (previousSelected) {
            previousSelected.removeAttribute("aria-selected");
        }

        // Set new selection
        if (item) {
            item.setAttribute("aria-selected", "true");
            item.scrollIntoView({ block: "nearest" });
        }
    }

    getSearchResults(searchTerm) {
        const searchUrl = `/search/suggest?q=${encodeURIComponent(
            searchTerm
        )}&section_id=predictive-search`;

        fetch(searchUrl)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text();
            })
            .then((text) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, "text/html");
                const sectionContent = doc.querySelector(
                    "#shopify-section-predictive-search"
                );

                if (sectionContent) {
                    this.predictiveSearchResults.innerHTML =
                        sectionContent.innerHTML;
                    this.highlightSearchTerms(searchTerm);
                    this.open();
                } else {
                    this.close();
                }
            })
            .catch((error) => {
                console.warn(
                    "PredictiveSearch: Error fetching search results:",
                    error
                );
                this.close();
            });
    }

    open() {
        this.predictiveSearchResults.style.display = "block";
        // Voor de CSS (.content:has(#predictive-search:not(.is-dicht))).
        this.predictiveSearchResults.classList.remove("is-dicht");
        this.input.setAttribute("aria-expanded", "true");
    }

    shouldStayOpen() {
        return this.input.value.trim().length >= this.minSearchLength;
    }

    close() {
        this.predictiveSearchResults.style.display = "none";
        this.predictiveSearchResults.classList.add("is-dicht");
        this.input.setAttribute("aria-expanded", "false");

        // Clear any selected items
        const selectedItem = this.predictiveSearchResults.querySelector(
            '[aria-selected="true"]'
        );
        if (selectedItem) {
            selectedItem.removeAttribute("aria-selected");
        }
    }

    highlightSearchTerms(searchTerm) {
        if (!searchTerm || searchTerm.length < 2) return;

        const resultsContainer = this.predictiveSearchResults.querySelector(
            "#predictive-search-results"
        );
        if (!resultsContainer) return;

        // Get all title elements that should be highlighted
        const titleElements = resultsContainer.querySelectorAll(
            ".predictive-search__title"
        );

        titleElements.forEach((titleElement) => {
            const originalText = titleElement.textContent;
            const highlightedText = this.highlightText(
                originalText,
                searchTerm
            );
            titleElement.innerHTML = highlightedText;
        });
    }

    highlightText(text, searchTerm) {
        if (!searchTerm || searchTerm.length < 2) return text;

        // Escape special regex characters in search term
        const escapedSearchTerm = searchTerm.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
        );

        // Create case-insensitive regex
        const regex = new RegExp(`(${escapedSearchTerm})`, "gi");

        // Replace matches with highlighted version
        return text.replace(regex, '<mark class="search-highlight">$1</mark>');
    }

    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }
}

// Register the custom element
customElements.define("predictive-search", PredictiveSearch);

// Initialize predictive search for existing elements
document.addEventListener("DOMContentLoaded", function () {
    const predictiveSearchElements =
        document.querySelectorAll("predictive-search");
    predictiveSearchElements.forEach((element) => {
        if (!element.initialized) {
            element.initialized = true;
        }
    });
});
